import * as React from "react";
import { FixedSizeGrid } from "react-window";
import faData from "../../../../shared/icon/fa-icons.json";
import data from "../../../../shared/icon/icons.json";
import {
  FontAwesomeIconType,
  IconLibrary,
  IconType,
  isFontAwesomeIconType,
  isMaterialIconType,
  KIcon,
  MaterialIconType
} from "../../../../shared/icon/k-icon";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";

interface IFaIconEntry {
  name: string;
  styles: FontAwesomeIconType[];
  previewStyle: FontAwesomeIconType;
  tags: string[];
  aliases?: string[];
}

interface IMaterialIconEntry {
  name: string;
  tags: string[];
}

interface IMaterialIconCatalog {
  icons: IMaterialIconEntry[];
}

interface IFaIconCatalog {
  icons: IFaIconEntry[];
}

type FaStyleFilter = "all" | FontAwesomeIconType;

export interface IPropertyFormIconState {
  searchText: string;
  iconName: string;
  containerWidth: number;
  iconLibrary: IconLibrary;
  faStyleFilter: FaStyleFilter;
  hoveredIconName: string | null;
}

export interface IPropertyFormIconProps {
  onChange: (value: string) => void;
  name: string;
  iconLibrary?: IconLibrary;
  iconType?: IconType;
  onLibraryChange?: (library: IconLibrary) => void;
  onIconTypeSync?: (iconType: FontAwesomeIconType | MaterialIconType) => void;
  /**
   * Whether the owning component actually has an `iconLibrary` property (i.e.
   * the runtime can render Font Awesome). Only the `Icon` component does; for
   * `Button`/`FloatButton` etc. the icon always renders Material, so the Font
   * Awesome tab is hidden to avoid offering a selection the runtime ignores.
   * Undefined defaults to `true` for backward compatibility.
   */
  supportsLibrary?: boolean;
}

interface IIconGridCellProps {
  columnIndex: number;
  rowIndex: number;
  style: React.CSSProperties;
}

interface IFaStyleFilterOption {
  key: FaStyleFilter;
  label: string;
}

const MATERIAL_ICON_CATALOG: IMaterialIconCatalog = data as IMaterialIconCatalog;
const FA_ICON_CATALOG: IFaIconCatalog = faData as IFaIconCatalog;

const LIBRARY_FALLBACK_ICON: Record<IconLibrary, string> = {
  material: "home",
  fontawesome: "house"
};

const FA_STYLE_FILTERS: IFaStyleFilterOption[] = [
  { key: "all", label: "All" },
  { key: "solid", label: "Solid" },
  { key: "regular", label: "Regular" },
  { key: "brands", label: "Brands" }
];

const TAB_BASE_STYLE: React.CSSProperties = {
  flex: 1,
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: 500,
  textAlign: "center",
  transition: "all 0.15s ease",
  borderRadius: "var(--radius-s)"
};

const TAB_ACTIVE_STYLE: React.CSSProperties = {
  ...TAB_BASE_STYLE,
  background: "var(--blue-1)",
  color: "var(--blue-6)",
  boxShadow: "var(--shadow-sm)"
};

const TAB_INACTIVE_STYLE: React.CSSProperties = {
  ...TAB_BASE_STYLE,
  background: "transparent",
  color: "var(--dark-7)"
};

const TAB_GROUP_STYLE: React.CSSProperties = {
  display: "flex",
  gap: "2px",
  marginBottom: "8px",
  padding: "2px",
  background: "var(--dark-1)",
  borderRadius: "var(--radius-m)"
};

const CHIP_BASE_STYLE: React.CSSProperties = {
  padding: "3px 10px",
  borderRadius: "var(--radius-pill)",
  fontSize: "11px",
  cursor: "pointer",
  transition: "all 0.15s ease",
  userSelect: "none"
};

const CHIP_ACTIVE_STYLE: React.CSSProperties = {
  ...CHIP_BASE_STYLE,
  background: "var(--blue-6)",
  color: "#ffffff"
};

const CHIP_INACTIVE_STYLE: React.CSSProperties = {
  ...CHIP_BASE_STYLE,
  background: "var(--dark-1)",
  color: "var(--dark-7)"
};

const CHIPS_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "4px",
  marginBottom: "8px"
};

const CELL_CONTAINER_BASE: React.CSSProperties = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  width: "100%",
  borderRadius: "var(--radius-l)",
  transition: "background 0.12s ease, border-color 0.12s ease",
  border: "1px solid transparent",
  boxSizing: "border-box"
};

const CELL_ICON_STYLE: React.CSSProperties = {
  color: "var(--dark-7)",
  fontSize: "20px",
  cursor: "pointer"
};

const SELECTED_PREVIEW_STYLE: React.CSSProperties = {
  color: "var(--dark-9)",
  fontSize: "20px",
  cursor: "pointer"
};

const COUNT_BADGE_STYLE: React.CSSProperties = {
  fontSize: "11px",
  color: "var(--dark-6)",
  padding: "0 4px",
  marginBottom: "4px"
};

const EMPTY_STATE_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "241px",
  color: "var(--dark-6)",
  fontSize: "12px",
  gap: "6px"
};

class PropertyFormIcon extends React.PureComponent<IPropertyFormIconProps, IPropertyFormIconState> {
  containerRef: React.RefObject<HTMLDivElement>;

  resizeObserver: ResizeObserver | null;

  constructor(props: IPropertyFormIconProps) {
    super(props);
    this.state = {
      searchText: "",
      iconName: this.props.name,
      containerWidth: 0,
      iconLibrary: this.props.iconLibrary === "fontawesome" ? "fontawesome" : "material",
      faStyleFilter: "all",
      hoveredIconName: null
    };
    this.containerRef = React.createRef();
    this.resizeObserver = null;
  }

  componentDidMount(): void {
    if (this.containerRef.current) {
      this.updateContainerWidth();
      this.resizeObserver = new ResizeObserver(this.updateContainerWidth);
      this.resizeObserver.observe(this.containerRef.current);
    }
  }

  componentDidUpdate(prevProps: IPropertyFormIconProps): void {
    if (prevProps.iconLibrary !== this.props.iconLibrary) {
      const next: IconLibrary = this.props.iconLibrary === "fontawesome" ? "fontawesome" : "material";
      if (this.state.iconLibrary !== next) {
        this.setState({ iconLibrary: next, searchText: "", faStyleFilter: "all" });
      }
    }
    if (prevProps.name !== this.props.name && this.state.iconName !== this.props.name) {
      this.setState({ iconName: this.props.name });
    }
  }

  componentWillUnmount(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  updateContainerWidth = (): void => {
    if (this.containerRef.current) {
      const width: number = this.containerRef.current.clientWidth - 16;
      this.setState({ containerWidth: width });
    }
  };

  sendContent = (val: string): void => {
    if (this.props.onChange) {
      this.props.onChange(val);
      this.setState({ iconName: val });
    }
  };

  sendFaIconWithStyleSync = (icon: IFaIconEntry): void => {
    if (this.props.onChange) {
      this.props.onChange(icon.name);
      this.setState({ iconName: icon.name });
    }
    if (this.props.onIconTypeSync) {
      const currentType: FontAwesomeIconType | undefined = isFontAwesomeIconType(this.props.iconType)
        ? this.props.iconType
        : undefined;
      const isCurrentSupported: boolean = currentType !== undefined && icon.styles.includes(currentType);
      if (!isCurrentSupported) {
        this.props.onIconTypeSync(icon.previewStyle);
      }
    }
  };

  private resolveIconNameForLibrary = (currentName: string, library: IconLibrary): string => {
    const catalog: { icons: { name: string }[] } = library === "material" ? MATERIAL_ICON_CATALOG : FA_ICON_CATALOG;
    const exists: boolean = catalog.icons.some((i) => i.name === currentName);
    if (exists) return currentName;
    return library === "material" ? LIBRARY_FALLBACK_ICON.material : LIBRARY_FALLBACK_ICON.fontawesome;
  };

  changeLibrary = (library: IconLibrary): void => {
    if (this.state.iconLibrary === library) {
      return;
    }
    const nextIconName: string = this.resolveIconNameForLibrary(this.state.iconName, library);
    const iconNameChanged: boolean = nextIconName !== this.state.iconName;
    this.setState({ iconLibrary: library, searchText: "", faStyleFilter: "all", iconName: nextIconName });
    if (this.props.onLibraryChange) {
      this.props.onLibraryChange(library);
    }
    if (iconNameChanged && this.props.onChange) {
      this.props.onChange(nextIconName);
    }
  };

  changeFaStyleFilter = (filter: FaStyleFilter): void => {
    if (this.state.faStyleFilter === filter) return;
    this.setState({ faStyleFilter: filter });
  };

  private compareBySearchRelevance = <T extends { name: string }>(search: string) => {
    return (a: T, b: T): number => {
      const nameA: string = a.name.toLowerCase();
      const nameB: string = b.name.toLowerCase();
      if (nameA.includes(search) && !nameB.includes(search)) return -1;
      if (!nameA.includes(search) && nameB.includes(search)) return 1;
      if (nameA.startsWith(search) && !nameB.startsWith(search)) return -1;
      if (nameA === search && nameB !== search) return -1;
      return nameA.localeCompare(nameB);
    };
  };

  private filterByNameOrTag = <T extends { name: string; tags: string[]; aliases?: string[] }>(
    entries: T[],
    search: string
  ): T[] => {
    if (!search) return entries.slice();
    return entries.filter((icon) => {
      if (icon.name.toLowerCase().includes(search)) return true;
      if (icon.tags.some((tag) => tag.toLowerCase().includes(search))) return true;
      if (icon.aliases && icon.aliases.some((alias) => alias.toLowerCase().includes(search))) return true;
      return false;
    });
  };

  getFilteredMaterialIcons = (): IMaterialIconEntry[] => {
    const search: string = this.state.searchText.toLowerCase();
    return this.filterByNameOrTag(MATERIAL_ICON_CATALOG.icons, search).sort(
      this.compareBySearchRelevance<IMaterialIconEntry>(search)
    );
  };

  getFilteredFaIcons = (): IFaIconEntry[] => {
    const search: string = this.state.searchText.toLowerCase();
    const filter: FaStyleFilter = this.state.faStyleFilter;
    const byStyle: IFaIconEntry[] =
      filter === "all" ? FA_ICON_CATALOG.icons : FA_ICON_CATALOG.icons.filter((icon) => icon.styles.includes(filter));
    return this.filterByNameOrTag(byStyle, search).sort(this.compareBySearchRelevance<IFaIconEntry>(search));
  };

  private getSelectedFaEntry = (): IFaIconEntry | undefined => {
    if (this.state.iconLibrary !== "fontawesome" || !this.state.iconName) return undefined;
    return FA_ICON_CATALOG.icons.find((i) => i.name === this.state.iconName);
  };

  private getActiveFaVariant = (entry: IFaIconEntry): FontAwesomeIconType => {
    if (isFontAwesomeIconType(this.props.iconType) && entry.styles.includes(this.props.iconType)) {
      return this.props.iconType;
    }
    return entry.previewStyle;
  };

  selectIconVariant = (entry: IFaIconEntry, variant: FontAwesomeIconType): void => {
    if (!entry.styles.includes(variant)) return;
    if (this.props.onIconTypeSync) {
      this.props.onIconTypeSync(variant);
    }
  };

  renderTabs = (): React.ReactNode => {
    // Components without an `iconLibrary` property (Button, FloatButton, …)
    // render Material only — hide the library switch so the panel never offers
    // a Font Awesome selection the runtime would drop (icons render broken).
    if (this.props.supportsLibrary === false) {
      return null;
    }
    return (
      <div style={TAB_GROUP_STYLE}>
        <div
          style={this.state.iconLibrary === "material" ? TAB_ACTIVE_STYLE : TAB_INACTIVE_STYLE}
          onClick={() => this.changeLibrary("material")}
        >
          Material Icons
        </div>
        <div
          style={this.state.iconLibrary === "fontawesome" ? TAB_ACTIVE_STYLE : TAB_INACTIVE_STYLE}
          onClick={() => this.changeLibrary("fontawesome")}
        >
          Font Awesome
        </div>
      </div>
    );
  };

  renderFaStyleFilter = (): React.ReactNode => {
    if (this.state.iconLibrary !== "fontawesome") return null;
    return (
      <div style={CHIPS_ROW_STYLE}>
        {FA_STYLE_FILTERS.map((opt) => (
          <div
            key={opt.key}
            style={this.state.faStyleFilter === opt.key ? CHIP_ACTIVE_STYLE : CHIP_INACTIVE_STYLE}
            onClick={() => this.changeFaStyleFilter(opt.key)}
          >
            {opt.label}
          </div>
        ))}
      </div>
    );
  };

  private getCellContainerStyle = (iconName: string): React.CSSProperties => {
    const isSelected: boolean = this.state.iconName === iconName;
    const isHovered: boolean = this.state.hoveredIconName === iconName;
    if (isSelected) {
      return {
        ...CELL_CONTAINER_BASE,
        background: "var(--blue-1)",
        borderColor: "var(--blue-5)"
      };
    }
    if (isHovered) {
      return { ...CELL_CONTAINER_BASE, background: "var(--dark-2)" };
    }
    return CELL_CONTAINER_BASE;
  };

  private onCellMouseEnter = (name: string): void => {
    if (this.state.hoveredIconName !== name) this.setState({ hoveredIconName: name });
  };

  private onCellMouseLeave = (name: string): void => {
    if (this.state.hoveredIconName === name) this.setState({ hoveredIconName: null });
  };

  renderMaterialCell = (icon: IMaterialIconEntry): React.ReactNode => {
    const isSelected: boolean = this.state.iconName === icon.name;
    return (
      <div
        style={this.getCellContainerStyle(icon.name)}
        title={icon.name}
        data-testid={`iconPickerOption-${icon.name}`}
        onMouseEnter={() => this.onCellMouseEnter(icon.name)}
        onMouseLeave={() => this.onCellMouseLeave(icon.name)}
        onClick={() => this.sendContent(icon.name)}
      >
        <KIcon
          style={{ ...CELL_ICON_STYLE, color: isSelected ? "var(--blue-6)" : CELL_ICON_STYLE.color }}
          iconName={icon.name}
          iconLibrary="material"
        />
      </div>
    );
  };

  renderFaCell = (icon: IFaIconEntry): React.ReactNode => {
    const isSelected: boolean = this.state.iconName === icon.name;
    return (
      <div
        style={this.getCellContainerStyle(icon.name)}
        title={icon.name}
        data-testid={`iconPickerOption-${icon.name}`}
        onMouseEnter={() => this.onCellMouseEnter(icon.name)}
        onMouseLeave={() => this.onCellMouseLeave(icon.name)}
        onClick={() => this.sendFaIconWithStyleSync(icon)}
      >
        <KIcon
          style={{ ...CELL_ICON_STYLE, color: isSelected ? "var(--blue-6)" : CELL_ICON_STYLE.color }}
          iconName={icon.name}
          iconLibrary="fontawesome"
          iconType={icon.previewStyle}
        />
      </div>
    );
  };

  renderSelectedVariantChips = (): React.ReactNode => {
    const entry: IFaIconEntry | undefined = this.getSelectedFaEntry();
    if (!entry || entry.styles.length <= 1) return null;
    const active: FontAwesomeIconType = this.getActiveFaVariant(entry);
    return (
      <div style={{ ...CHIPS_ROW_STYLE, marginTop: "4px", marginBottom: "0" }}>
        {entry.styles.map((style) => (
          <div
            key={style}
            style={active === style ? CHIP_ACTIVE_STYLE : CHIP_INACTIVE_STYLE}
            onClick={() => this.selectIconVariant(entry, style)}
          >
            {style.charAt(0).toUpperCase() + style.slice(1)}
          </div>
        ))}
      </div>
    );
  };

  renderSelectedPreview = (): React.ReactNode => {
    const isFa: boolean = this.state.iconLibrary === "fontawesome";
    if (!this.state.iconName) {
      return null;
    }
    if (isFa) {
      const entry: IFaIconEntry | undefined = FA_ICON_CATALOG.icons.find((i) => i.name === this.state.iconName);
      const previewStyle: FontAwesomeIconType = isFontAwesomeIconType(this.props.iconType)
        ? this.props.iconType
        : entry?.previewStyle || "solid";
      return (
        <KIcon
          style={SELECTED_PREVIEW_STYLE}
          iconName={this.state.iconName}
          iconLibrary="fontawesome"
          iconType={previewStyle}
        />
      );
    }
    const materialType: MaterialIconType | undefined = isMaterialIconType(this.props.iconType)
      ? this.props.iconType
      : undefined;
    return (
      <KIcon
        style={SELECTED_PREVIEW_STYLE}
        iconName={this.state.iconName}
        iconLibrary="material"
        iconType={materialType}
      />
    );
  };

  renderCountBadge = (filtered: number, total: number): React.ReactNode => {
    const label: string = filtered === total ? `${total} icons` : `${filtered} of ${total} icons`;
    return <div style={COUNT_BADGE_STYLE}>{label}</div>;
  };

  renderEmptyState = (): React.ReactNode => {
    return (
      <div style={EMPTY_STATE_STYLE}>
        <KuikaSvg name="search" color="var(--dark-5)" width={24} height={24} />
        <div>
          No icons match
          {this.state.searchText ? ` "${this.state.searchText}"` : ""}
        </div>
      </div>
    );
  };

  render(): React.ReactNode {
    const isFa: boolean = this.state.iconLibrary === "fontawesome";
    const filteredMaterial: IMaterialIconEntry[] = isFa ? [] : this.getFilteredMaterialIcons();
    const filteredFa: IFaIconEntry[] = isFa ? this.getFilteredFaIcons() : [];
    const totalCount: number = isFa ? filteredFa.length : filteredMaterial.length;
    const catalogCount: number = isFa ? FA_ICON_CATALOG.icons.length : MATERIAL_ICON_CATALOG.icons.length;

    const GRID_HEIGHT: number = 241;
    const { containerWidth } = this.state;
    const ICON_SIZE: number = 44;
    const COLUMN_COUNT: number = Math.max(1, Math.floor(containerWidth / ICON_SIZE));

    const Cell = ({ columnIndex, rowIndex, style }: IIconGridCellProps): React.ReactNode => {
      const index: number = rowIndex * COLUMN_COUNT + columnIndex;
      if (index >= totalCount) {
        return null;
      }
      return (
        <div style={{ ...style, padding: "2px" }}>
          {isFa ? this.renderFaCell(filteredFa[index]) : this.renderMaterialCell(filteredMaterial[index])}
        </div>
      );
    };

    return (
      <>
        <div className="kuika_icon_component__selected-icon">
          <KuikaSmallMediumText className="selected-text">Selected Icon:</KuikaSmallMediumText>
          <KuikaTooltip content={this.state.iconName}>
            <div className="selected-icon__wrapper">
              {this.renderSelectedPreview()}
              <KuikaSmallMediumText className="selected-text" data-testid="iconPickerSelectedIconName">
                {this.state.iconName}
              </KuikaSmallMediumText>
            </div>
          </KuikaTooltip>
        </div>
        {this.renderSelectedVariantChips()}
        {this.renderTabs()}
        {this.renderFaStyleFilter()}
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <KuikaInput
            data-testid="iconPickerSearchInput"
            placeholder="Search by name or tag..."
            onChange={(e) => this.setState({ searchText: e.target.value })}
            value={this.state.searchText}
            style={{ width: "100%" }}
          />
          <KuikaTooltip content="Clear icon">
            <KuikaButton
              type="tertiary"
              color="neutral"
              onClick={() => this.sendContent("")}
              style={{ cursor: "pointer" }}
            >
              <KuikaSvg name="trash" color="var(--red-5)" width={20} height={20} />
            </KuikaButton>
          </KuikaTooltip>
        </div>
        {this.renderCountBadge(totalCount, catalogCount)}
        <div
          ref={this.containerRef}
          style={{
            background: "var(--dark-0)",
            borderRadius: "var(--radius-l)",
            width: "100%",
            height: "257px",
            padding: "8px",
            overflow: "hidden"
          }}
        >
          {containerWidth > 0 && totalCount > 0 && (
            <FixedSizeGrid
              columnCount={COLUMN_COUNT}
              columnWidth={ICON_SIZE}
              height={GRID_HEIGHT}
              rowCount={Math.ceil(totalCount / COLUMN_COUNT)}
              rowHeight={ICON_SIZE}
              width={containerWidth}
            >
              {Cell}
            </FixedSizeGrid>
          )}
          {containerWidth > 0 && totalCount === 0 && this.renderEmptyState()}
        </div>
      </>
    );
  }
}

const propertyFormIcon = PropertyFormIcon;
export { propertyFormIcon as PropertyFormIcon };
